import { fn } from '../utils';
import { CompatibilityService } from "../services/compatibilityservice";
import { IFDRW_Workflow_Setup_WS, IFDRW_WF_User, PreviewType } from "./rwfpages.generated";
import { IRwfSettings } from "./rwf.backend";
import { INavUrlInfo } from "../navdata";
import { IRoutingService, Param_Company, Param_NavUrl } from "../services/autoresolveservice";
import { IPreviewSettings } from '@common/interfaces';
import { InfomaClientIntegration } from '../infomaclient';
export type TBootsrapResolveAction = (r: IRwfBootstrapParameters) => void | PromiseLike<any> | any;

/** Root Parameter Provisioning */
export class RwfBootstrap {
    /** Default Annotated Resolve Function */
    static readonly Resolve = ["CompatibilityService", ($c: CompatibilityService) => $c.resolve("Rwf")("RwfBootstrap").Resolve];
    constructor($c: CompatibilityService) {
        EnsureQueryFixApplied($c.resolve("Routing"));
        const NavUrl = $c.resolve("NavUrl");
        const routing = $c.resolve("Routing");
        const rwfBackend = $c.resolve("RwfBackend");
        const urlAlreadySet = NavUrl.ExternalDefaultUrl;
        if (urlAlreadySet) {
            var result = rwfBackend.BootstrapRwf(NavUrl, routing.Search(Param_Company));
        } else {
            result = rwfBackend.BootstrapRwf(routing.Search(Param_NavUrl), routing.Search(Param_Company));
        }
        result.then((bParams) => {
            this.resolvedParameters = bParams;
            if (!urlAlreadySet) {
                NavUrl.SetDefaultUrl(bParams.NavUrlInfo);
                routing.SetCurrentNavContext(bParams.NavUrlInfo, bParams.RwfSettings.DefaultNavUrl, true);
            }
        });
        this.Resolve = result.then(resolvedBootstrapParameters => {
            const actions = this.actions.slice();
            delete this.actions;
            return $c.all(actions.map(item =>
                item(resolvedBootstrapParameters))).then(() =>
                    resolvedBootstrapParameters);
        });
    }

    /** Promise to be resolved upon root parameters available */
    readonly Resolve: PromiseLike<IRwfBootstrapParameters>;

    /** Register Function to execute when Parameters resolved, before 'Resolve' gets resolved if not yet done
     * if called after resolution execute via resolve implicitly! */
    public OnResolved(action: TBootsrapResolveAction, allowSync?: boolean) {
        if (this.actions) {
            this.actions.push(action);
        } else if (allowSync && this.resolvedParameters) {
            action(this.resolvedParameters);
        } else {
            this.Resolve.then(action);
        }
    }
    /** actions to execute on resolve, gets deleted upon resolution */
    private actions = new Array<TBootsrapResolveAction>();

    EnsureResolved() {
        if (!this.resolvedParameters) {
            throw new Error('RWF Application is not yet bootstrapped');
        }
        return this.resolvedParameters;
    }
    private resolvedParameters: IRwfBootstrapParameters;
}

/** Explicit Client Credential Types (IFDRW_Workflow_Setup_WS.ClientCredentialType) */
export type TClientCredentialType = "Windows" | "NavUserPassword" | "NavUser" | "FullExternal" | "";

/** root parameters */
export interface IRwfBootstrapParameters {
    /** Nav Url Context */
    readonly NavUrlInfo: INavUrlInfo;

    /*  Worflow Setup */
    readonly RwfSetup: IFDRW_Workflow_Setup_WS;

    /** Workflow User */
    readonly RwfUser: IFDRW_WF_User;

    /** Custom Backend Settings */
    readonly RwfSettings: IRwfSettings;

    /** Optional Root Integration Point for Infoma Advanced Client functionality in case of site is running in InfomaAdvanced Client*/
    readonly InfomaClient?: InfomaClientIntegration;
}

/** Workaround for obscure "url with hasbang rewrite" problem:
* call "http://infomarwf.wetteraukreis.local/index.html#/docinfo?id=0D163F4590344C99A31406211D464A79&company=WKDOPPIKproduktiv" from NAV
* gets rewritten to "http://infomarwf.wetteraukreis.local/index.html?id=0D163F4590344C99A31406211D464A79&company=WKDOPPIKproduktiv#/docinfo"
* when retrieved on client side for obscure, unknwon reason!
* (and angular ignores "pre hashbang" params)
* @param routing
*/
function propagateQueryStrings(routing: IRoutingService) {
    try {
        var query = window.location.search.substring(1),
            search = routing.Search();
        var vars = query.split("&").map(q => (q || "").split("=")).map(s => {
            return {
                name: s[0],
                value: decodeURIComponent(s[1])
            };
        }).filter(s => s.name && s.value && !search[s.name]);
        routing.LockReload(() => vars.forEach(v => routing.search(v.name, v.value)));
    } catch (err) {
        // alert(err);
    }
}

export function ResolvePreviewSettings(rwfBootstrap: IRwfBootstrapParameters, settings?: IPreviewSettings) {
    settings = settings || <IPreviewSettings>{};
    let pType = settings.PreviewType = rwfBootstrap.RwfSetup.PreviewType;

    settings.annotationUser = rwfBootstrap.RwfUser.UserName || rwfBootstrap.RwfUser.ID;
    // Validate PreviewType
    switch (pType) {
        case PreviewType.DMSanmerken:
            // License check
            if (rwfBootstrap.RwfSetup.IsAnnotationLicensed == false) {
                settings.PreviewType = PreviewType.DMSviewer;
                settings.enableReadOnlyMode = true;
            } else {
                settings.enableReadOnlyMode = false;
            }
            break;
        case PreviewType.DMSviewer:
            settings.enableReadOnlyMode = true;
            break;

        default:
        // noting to do
    }
    return settings;
}

const EnsureQueryFixApplied = fn.once(propagateQueryStrings);
